/**
 * @fileoverview Bulk-clearing missing-notes notices from an instructor's lists:
 * Notes owed, a class's Lessons tab and a program's Lessons tab
 * (lib/notesClearance, backend services/notes_clearances).
 *
 * The instructor ticks sessions and:
 *
 * - **Clears** them. A meeting of an organization's class is *asked about*: it
 *   stays on the list, tagged "Clear requested", until an admin approves or
 *   declines. A session no organization tracks (their own student or class) is
 *   cleared at once. The server says which each row is (`notes_clear_mode`).
 * - **Withdraws** their own request, or **puts back** a session they cleared
 *   themselves. A session the organization cleared is not theirs to put back.
 */

import { useContext, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import { AuthContext } from "@/App";
import useRowSelection from "@/hooks/useRowSelection";
import { NotesClearDialog, NotesClearToolbar, SelectBox } from "@/components/studio/NotesClearing";
import { isCleared, isRequested, plural, sendInPages, toastResult } from "@/lib/notesClearance";

/**
 * @param {{
 *   rows: Array<object>, onChanged?: () => void,
 *   keyOf?: (row: object) => string, testId?: string,
 * }} options rows are the visible rows, each with `occurrence_id`,
 *   `missing` or `notes_cleared`, and the server's `notes_clearance` and
 *   `notes_clear_mode`.
 */
export default function useInstructorNotesClear({ rows, onChanged, keyOf = (r) => r.occurrence_id, testId = "notes-clear" }) {
  const { user } = useContext(AuthContext) || {};
  const me = user?.user_id;

  const mine = (row) => {
    const c = row.notes_clearance;
    if (!me || !c || c.requested_by !== me) return false;
    return isRequested(row) || (isCleared(row) && c.cleared_by_role === "instructor");
  };
  const clearable = (row) => row.missing && !isRequested(row) && Boolean(row.notes_clear_mode);

  const acting = useMemo(
    () => rows.filter((r) => r.occurrence_id && (clearable(r) || mine(r))),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `mine` reads only `me`
    [rows, me],
  );
  const byKey = useMemo(() => new Map(acting.map((r) => [keyOf(r), r])), [acting, keyOf]);
  const selection = useRowSelection(acting.map(keyOf));
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  const chosen = [...selection.selected].map((k) => byKey.get(k)).filter(Boolean);
  const toClear = chosen.filter(clearable);
  const toAsk = toClear.filter((r) => r.notes_clear_mode === "request");
  const toTakeBack = chosen.filter(mine);

  const clear = async (reason) => {
    setBusy(true);
    try {
      const result = await sendInPages(
        toClear.map((r) => r.occurrence_id),
        (page) => api.post("/notes-owed/clear", { occurrence_ids: page, ...(reason ? { reason } : {}) }),
        ["requested", "cleared"],
      );
      const asked = result.requested.length;
      const cleared = result.cleared.length;
      const message = [
        asked ? `Asked your organization to clear ${plural(asked, "session")}` : null,
        cleared ? `${plural(cleared, "session")} cleared` : null,
      ]
        .filter(Boolean)
        .join(". ");
      toastResult(message, asked + cleared, result.skipped);
      selection.clear();
      setAsking(false);
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't clear those sessions"));
      if (err.partial) onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const takeBack = async () => {
    setBusy(true);
    try {
      const result = await sendInPages(
        toTakeBack.map((r) => ({ occurrence_id: r.occurrence_id, class_id: r.class_id || null })),
        (page) => api.post("/notes-owed/restore", { sessions: page }),
        ["restored"],
      );
      toastResult(`${plural(result.restored.length, "session")} back on your notes list`, result.restored.length, result.skipped);
      selection.clear();
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't put those sessions back"));
    } finally {
      setBusy(false);
    }
  };

  const allAsk = toClear.length > 0 && toAsk.length === toClear.length;
  const actions = [
    toClear.length > 0 && {
      id: "clear",
      label: allAsk ? `Ask to clear ${toClear.length}` : `Clear ${toClear.length}`,
      primary: true,
      onPress: () => setAsking(true),
    },
    toTakeBack.length > 0 && {
      id: "restore",
      label: `Put back ${toTakeBack.length}`,
      onPress: takeBack,
      disabled: busy,
    },
  ].filter(Boolean);

  const checkbox = (row) => {
    const key = keyOf(row);
    if (!byKey.has(key)) return null;
    return (
      <SelectBox
        checked={selection.selected.has(key)}
        onChange={() => selection.toggle(key)}
        label={`Select ${row.display_name || row.title || "session"} on ${row.date || row.scheduled_date}`}
        testId={`${testId}-row-${key}`}
      />
    );
  };

  const toolbar = (
    <NotesClearToolbar
      selection={selection}
      actions={actions}
      hint="Tick sessions that need no notes to clear them."
      testId={testId}
    />
  );

  const direct = toClear.length - toAsk.length;
  const dialogs = (
    <NotesClearDialog
      open={asking}
      onOpenChange={setAsking}
      title={allAsk ? `Ask to clear ${plural(toClear.length, "session")}?` : `Clear ${plural(toClear.length, "session")}?`}
      description="For sessions that need no notes: a trip, a concert, an assembly that took the period. Nothing is marked as taught."
      noteLabel="Reason (optional)"
      notePlaceholder="e.g. Field trip"
      confirmLabel={allAsk ? "Ask to clear" : "Clear"}
      busy={busy}
      onConfirm={clear}
      testId={`${testId}-dialog`}
    >
      <ul className="flex flex-col gap-1 font-sans text-[14px] text-[var(--text-secondary)]" data-testid={`${testId}-split`}>
        {toAsk.length > 0 && (
          <li>
            {plural(toAsk.length, "session")} of your organization&rsquo;s classes go to an admin to approve. They stay on
            your list, marked as asked, until then.
          </li>
        )}
        {direct > 0 && <li>{plural(direct, "session")} of your own {direct === 1 ? "is" : "are"} cleared now.</li>}
      </ul>
    </NotesClearDialog>
  );

  return { checkbox, toolbar, dialogs, selection };
}
